import React, { Component, Fragment } from 'react';
import { shape, string } from 'prop-types';

import errorCodes from 'util/auth/errorCodes';
import CreateUserProfileMutation from 'components/CreateUserProfileMutation';
import { COMPLETE_SIGNUP_INSTRUCTION } from 'constants/auth0';
import { SITE_NAME } from 'constants/site';
import {
  updateUserData,
  getRedirectUrl,
  clearRedirectUrl,
  setAuthCookies
} from 'util/auth';
import styles from './styles';

export class UserCompleteSignup extends Component {
  constructor(props) {
    super(props);

    this.state = {
      error: false,
      errorMessage: '',
      loading: false,
      // spot.im does not function well with + characters, so we are preventing them
      wipDisplayName: props.user.displayName.replace(/\+/g, '')
    };
  }

  handleChangeDisplayName = e => {
    const {
      target,
      target: { value }
    } = e;
    target.setCustomValidity('');
    this.setState({ wipDisplayName: value });
  };

  handleError(error) {
    const errorMessage =
      error.message.indexOf(errorCodes.DUPLICATE_DISPLAY_NAME) !== -1
        ? 'This Display Name is already taken. Try Another?'
        : 'Something went wrong. Please try again.';
    this.setState({
      loading: false,
      error: true,
      errorMessage
    });
  }

  handleInvalidDisplayName = e => {
    const {
      target,
      target: {
        validity: { valid },
        value
      }
    } = e;
    target.setCustomValidity('');
    if (!valid) {
      const message = value
        ? 'Please do not use the + character in your display name.'
        : 'Please choose a display name.';
      target.setCustomValidity(message);
    }
  };

  handleSubmit = (e, createUserProfileUser) => {
    e.preventDefault();

    this.setState({ loading: true }, () => {
      this.handleCallMutation(createUserProfileUser);
    });
  };

  handleCallMutation(createUserProfileUser) {
    const { wipDisplayName } = this.state;
    const { user } = this.props;
    const redirectUrl = getRedirectUrl();

    return createUserProfileUser({
      variables: { ...user, displayName: wipDisplayName }
    })
      .then(async ({ data, error }) => {
        if (error) {
          this.handleError(error);
        } else if (data.createUser.user) {
          const { atid } = data.createUser.user;
          // Update user cookie to include `atid`, if it exists.
          if (atid) {
            await updateUserData({ user, atid });
          }
          clearRedirectUrl();
          // since we are redirecting away and "finished" with the auth process (not hitting /auth/signed-in)
          // we need to mark user creation as complete so we can key off it once the client side redirect has finished
          setAuthCookies(null, null, { ...user, creationComplete: true });
          window.location.replace(redirectUrl);
        }
      })
      .catch(error => {
        this.handleError(error);
      });
  }

  render() {
    const { error, errorMessage, loading, wipDisplayName } = this.state;

    return (
      <Fragment>
        <style jsx>{styles}</style>
        <div className="UserCompleteSignup">
          <h1 data-test="siteName">Welcome to {SITE_NAME}</h1>
          <p>{COMPLETE_SIGNUP_INSTRUCTION}</p>
          <CreateUserProfileMutation>
            {createUserProfileUser => (
              <form
                className="card"
                data-test="displayNameForm"
                onSubmit={e => this.handleSubmit(e, createUserProfileUser)}
              >
                <h2>Choose a Display Name</h2>
                <p>
                  Your Display Name is shown publicly as your username for
                  comments.
                  <em>
                    You can always change your Display Name later under your
                    account settings.
                  </em>
                </p>
                <input
                  className="UserCompleteSignup__input"
                  data-test="displayNameField"
                  onChange={this.handleChangeDisplayName}
                  onInvalid={this.handleInvalidDisplayName}
                  pattern="[^+]+"
                  required
                  type="text"
                  value={wipDisplayName}
                />
                {error ? (
                  <div
                    className="UserCompleteSignup__error"
                    data-test="displayNameError"
                  >
                    {errorMessage}
                  </div>
                ) : null}

                <button
                  className="button"
                  data-test="displayNameSubmit"
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Submit'}
                </button>
              </form>
            )}
          </CreateUserProfileMutation>
        </div>
      </Fragment>
    );
  }
}

UserCompleteSignup.propTypes = {
  user: shape({
    auth0User: string.isRequired,
    // communityId is not set for new users; assigned upon creation.
    communityId: string,
    displayName: string.isRequired,
    email: string.isRequired,
    image: string.isRequired,
    originatingSite: string.isRequired
  }).isRequired
};

export default UserCompleteSignup;
